// File Metadata:
// Filename: NewUserForm.js
// Version: 1.00
// Created: 2022-09-29 09:53:53
// Last Modified: 2025-04-01 17:27:27
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { useState, useEffect } from 'react'
import { useAddNewUserMutation } from '../api/usersApiSlice'
import { useNavigate } from 'react-router-dom'
import { 
    Paper, Stack, TextField, FormControl, 
    InputLabel, Select, MenuItem, Button,
    Alert, Grid, FormGroup, FormControlLabel,
    Switch, Typography 
} from '@mui/material'
import { useGetCrewsQuery } from '../api/crewsApiSlice'  // Fix import path

const USER_REGEX = /^[A-z]{3,20}$/
const PWD_REGEX = /^[A-z0-9!@#$%]{4,12}$/

const NewUserForm = () => {
    const [addNewUser, { isLoading, isSuccess, isError, error }] = useAddNewUserMutation()
    const navigate = useNavigate()

    const [formData, setFormData] = useState({
        username: '',
        password: '',
        employeeId: '',
        roles: {
            Employee: true,
            Voorman: false,
            Coordinator: false,
            Manager: false,
            Admin: false
        },
        profile: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            department: '',
            position: '',
            emergencyContact: {
                name: '',
                relationship: '',
                phone: ''
            }
        },
        crew: {
            type: 'weekday',
            isTemporary: false
        }
    })

    const [validUsername, setValidUsername] = useState(false)
    const [validPassword, setValidPassword] = useState(false)
    const [employmentType, setEmploymentType] = useState('ARBEIDER')
    const [crewId, setCrewId] = useState('')

    const { data: crews = [], isLoading: isLoadingCrews } = useGetCrewsQuery()

    useEffect(() => {
        setValidUsername(USER_REGEX.test(formData.username))
    }, [formData.username])

    useEffect(() => {
        setValidPassword(PWD_REGEX.test(formData.password))
    }, [formData.password])

    useEffect(() => {
        if (isSuccess) {
            navigate('/dash/users')
        }
    }, [isSuccess, navigate])

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const handleProfileChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            profile: {
                ...prev.profile,
                [field]: value
            }
        }))
    }

    const handleRoleToggle = (role) => {
        setFormData(prev => ({
            ...prev,
            roles: {
                ...prev.roles,
                [role]: !prev.roles[role]
            }
        }))
    }

    const isCrewRequired = employmentType === 'ARBEIDER'

    const canSave = [
        formData.username?.length,
        formData.password?.length,
        formData.roles?.length,
        employmentType,
        ...(isCrewRequired ? [crewId?.length] : []),
        !isLoading,
        !isLoadingCrews
    ].every(Boolean)

    const onSaveUserClicked = async (e) => {
        e.preventDefault()
        if (canSave) {
            await addNewUser(formData)
        }
    }

    return (
        <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
            <form onSubmit={onSaveUserClicked}>
                <Stack spacing={3}>
                    <Typography variant="h6">New User</Typography>

                    {isError && (
                        <Alert severity="error">
                            {error?.data?.message}
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        {/* Basic Info */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                required
                                label="Username"
                                value={formData.username}
                                onChange={(e) => handleInputChange('username', e.target.value)}
                                error={formData.username && !validUsername}
                                helperText="3-20 letters"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                required
                                label="Employee ID"
                                value={formData.employeeId}
                                onChange={(e) => handleInputChange('employeeId', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                type="password"
                                label="Password"
                                value={formData.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                error={formData.password && !validPassword}
                                helperText="4-12 chars including !@#$%"
                            />
                        </Grid>

                        {/* Profile Info */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                required
                                label="First Name"
                                value={formData.profile.firstName}
                                onChange={(e) => handleProfileChange('firstName', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                required
                                label="Last Name"
                                value={formData.profile.lastName}
                                onChange={(e) => handleProfileChange('lastName', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={formData.profile.email}
                                onChange={(e) => handleProfileChange('email', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Phone"
                                value={formData.profile.phone}
                                onChange={(e) => handleProfileChange('phone', e.target.value)}
                            />
                        </Grid>

                        {/* Department & Position */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Department"
                                value={formData.profile.department}
                                onChange={(e) => handleProfileChange('department', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Position"
                                value={formData.profile.position}
                                onChange={(e) => handleProfileChange('position', e.target.value)}
                            />
                        </Grid>

                        {/* Roles */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                Roles
                            </Typography>
                            <FormGroup row>
                                {Object.keys(formData.roles).map(role => (
                                    <FormControlLabel
                                        key={role}
                                        control={
                                            <Switch
                                                checked={formData.roles[role]}
                                                onChange={() => handleRoleToggle(role)}
                                                disabled={role === 'Employee'}
                                            />
                                        }
                                        label={role}
                                    />
                                ))}
                            </FormGroup>
                        </Grid>

                        {/* Employment Type */}
                        <Grid item xs={12}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Employment Type</InputLabel>
                                <Select
                                    value={employmentType}
                                    onChange={(e) => setEmploymentType(e.target.value)}
                                >
                                    <MenuItem value="ARBEIDER">Arbeider</MenuItem>
                                    <MenuItem value="BEDIENDE">Bediende</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Crew Assignment */}
                        {employmentType === 'ARBEIDER' && (
                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Crew Assignment</InputLabel>
                                    <Select
                                        value={crewId}
                                        onChange={(e) => setCrewId(e.target.value)}
                                        required={isCrewRequired}
                                        disabled={isLoadingCrews}
                                    >
                                        {isLoadingCrews ? (
                                            <MenuItem disabled>Loading crews...</MenuItem>
                                        ) : (
                                            crews.map(crew => (
                                                <MenuItem key={crew.id} value={crew.id}>
                                                    {crew.name}
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>

                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!canSave}
                    >
                        Save User
                    </Button>
                </Stack>
            </form>
        </Paper>
    )
}

export default NewUserForm
